/**
 * Rybbit Analytics integration — canonical home of all Rybbit logic.
 *
 * Ported from tazio-fe (src/utilities/integrations/rybbit.js) and adapted for
 * salabam-app. Runs as a SECOND analytics pipeline alongside GTM; events reach
 * it through the `custom_event` bus (see ./index.js) plus a few direct calls.
 *
 * SDK loading: <script> tag in public/index.html, conditionally injected when
 *              VUE_APP_RYBBIT_ENABLED === 'true'.
 * Global:      window.rybbit
 * Env vars:    VUE_APP_RYBBIT_ENABLED  ('true' to load the script)
 *              VUE_APP_RYBBIT_URL      (script + tracking host)
 *              VUE_APP_RYBBIT_SITE_ID  (Rybbit site identifier)
 *
 * Naming convention for events: `<feature>:<phase>:<action>` with `:`.
 * Site is NOT part of the event name (Rybbit already has `site_id`).
 *
 * Privacy contract (form events): `data` may contain ONLY `type`, `reason?`,
 * `message?`, `invalid_fields?` (field NAMES only). No form field VALUES, no PII.
 * Suspicious keys produce a console.warn in dev. Email addresses found in free
 * text (e.g. event labels) are md5-hashed before being sent to Rybbit.
 *
 * Payload size: Rybbit `/api/track` enforces a 2048 char limit on `properties`.
 * Strategy: progressive key drop, preserving `type`/`reason`.
 */

import md5 from 'md5'

// salabam-local equivalents of tazio's commons helpers --------------------
const is_development = () => process.env.VUE_APP_MODE !== 'production'
const deep_clone = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (e) {
    return obj
  }
}

// ---------------------------------------------------------------------------
// Configuration & constants
// ---------------------------------------------------------------------------

export const PROPERTIES_LIMIT = 2048

export const SUSPICIOUS_KEYS = [
  'email', 'phone', 'mobile', 'tel', 'telefono', 'password',
  'fiscal_code', 'cf', 'codice_fiscale', 'birth', 'birthdate', 'birthday',
  'address', 'indirizzo', 'city', 'citta', 'cap', 'postal_code',
  'name', 'nome', 'surname', 'cognome', 'firstname', 'first_name',
  'lastname', 'last_name',
]

// Keys preserved when forced to fallback (essential analytics keys).
const ESSENTIAL_KEYS = ['type', 'reason']

// Keys dropped progressively (in order) when payload exceeds the limit.
const DROPPABLE_KEYS = ['message']

// Matches email addresses inside arbitrary strings (for md5 redaction).
const EMAIL_REGEX = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g

/**
 * Rybbit configuration.
 * Exported so callers/tests can inspect the live mapping.
 */
export const RYBBIT_CONFIG = {
  // GA4 event names → Rybbit-normalized names
  eventMap: {
    'confirmedCheckout': 'checkout_completed',
    'confirmedOrder': 'checkout_completed',
    'failedCheckout': 'checkout_failed',
    'initiateCheckout': 'checkout_started',
    'begin_checkout': 'checkout_started',
    'add_to_cart': 'cart_add',
    'purchase': 'checkout_completed',
    'book': 'booking_completed',
  },
  // Events to drop (noisy or duplicated). salabam keeps checkout/order
  // conversions. `welfare` is noise; `formSubmit` is the flat GTM event —
  // Rybbit gets the richer `<form>:submit:success|fail` lifecycle instead.
  skipEvents: [
    'welfare',
    'formSubmit',
  ],
  // Events shaped as item lists (use min-price aggregation)
  itemListEvents: ['view_item', 'view_item_list'],
}

// ---------------------------------------------------------------------------
// PII redaction helpers
// ---------------------------------------------------------------------------

/**
 * Replace any email address found in a string with its md5 hash. Non-string
 * input is returned untouched. Keeps per-user correlation possible in Rybbit
 * without storing plaintext PII.
 */
export function _hash_emails(value) {
  if (typeof value !== 'string' || !value) return value
  return value.replace(EMAIL_REGEX, (match) => md5(match.toLowerCase()))
}

// ---------------------------------------------------------------------------
// Safety net — payload size & PII redaction
// ---------------------------------------------------------------------------

export function _warn_if_suspicious(props) {
  if (!is_development() || !props || typeof props !== 'object') return
  const found = Object.keys(props).filter((k) =>
    SUSPICIOUS_KEYS.includes(k.toLowerCase()),
  )
  if (found.length) {
    // eslint-disable-next-line no-console
    console.warn(
      '[rybbit] suspicious keys in props — never pass form field values:',
      found,
    )
  }
}

export function _serialized_length(payload) {
  try {
    return JSON.stringify(payload).length
  } catch (e) {
    return 0
  }
}

/**
 * Reduce the payload to fit within PROPERTIES_LIMIT.
 *
 * Strategy: deep-clone, drop keys in priority order (DROPPABLE_KEYS first, then
 * any non-essential top-level key, then any array sub-collection of nested
 * objects). When everything else fails, keep only ESSENTIAL_KEYS.
 */
export function _drop_to_fit(payload) {
  if (_serialized_length(payload) <= PROPERTIES_LIMIT) return payload

  const reduced = deep_clone(payload)

  const drop_order = [
    ...DROPPABLE_KEYS,
    ...Object.keys(reduced).filter(
      (k) => !ESSENTIAL_KEYS.includes(k) && !DROPPABLE_KEYS.includes(k),
    ),
  ]
  for (const key of drop_order) {
    if (!(key in reduced)) continue
    const value = reduced[key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const sub of Object.keys(value)) {
        if (Array.isArray(value[sub])) {
          delete value[sub]
          if (_serialized_length(reduced) <= PROPERTIES_LIMIT) {
            if (is_development()) {
              // eslint-disable-next-line no-console
              console.warn(`[rybbit] payload > ${PROPERTIES_LIMIT} char, dropped: ${key}.${sub}`)
            }
            return reduced
          }
        }
      }
    }
    delete reduced[key]
    if (_serialized_length(reduced) <= PROPERTIES_LIMIT) {
      if (is_development()) {
        // eslint-disable-next-line no-console
        console.warn(`[rybbit] payload > ${PROPERTIES_LIMIT} char, dropped: ${key}`)
      }
      return reduced
    }
  }

  if (is_development()) {
    // eslint-disable-next-line no-console
    console.warn(`[rybbit] payload > ${PROPERTIES_LIMIT} char, fallback to essentials only`)
  }
  const fallback = {}
  for (const k of ESSENTIAL_KEYS) {
    if (k in reduced) fallback[k] = reduced[k]
  }
  return fallback
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function rybbit_is_available() {
  return Boolean(window.rybbit)
}

/**
 * Send a Rybbit event with built-in safety net:
 *   - warn (dev only) on suspicious PII keys in props
 *   - drop_to_fit when serialized props exceed 2048 char
 *
 * @param {string} event_name
 * @param {Object} [props]
 */
export function rybbit_event(event_name, props = {}) {
  if (!rybbit_is_available()) return
  if (!event_name || typeof event_name !== 'string') return
  _warn_if_suspicious(props)
  const safe_props = _drop_to_fit(props)
  window.rybbit.event(event_name, safe_props)
}

/**
 * Naming-consistent alias of `rybbit_event`.
 */
export function rybbit_track_event(event_name, props) {
  return rybbit_event(event_name, props || {})
}

/**
 * Normalize and forward an event coming from `custom_event(...)`.
 * Handles the conversion from GA4-shaped payloads to Rybbit's flat schema,
 * applies the eventMap, optionally rewrites SSO into session_start, and drops
 * events listed in `skipEvents`.
 *
 * @param {string|Object} action  - Event name OR a complete GA4 event object.
 * @param {string} [label]
 * @param {number} [value]
 * @param {string} [category]
 */
export function rybbit_event_from_custom(action, label, value, category) {
  if (!rybbit_is_available()) return
  if (!_rybbit_validate_action(action)) return

  // 1. Parsing — extract eventName + props depending on the action shape
  let parsed
  if (typeof action === 'object') {
    parsed = _rybbit_parse_ga4_event(action)
  } else {
    parsed = _rybbit_parse_standard_event(action, label, value, category)
  }

  // 2. Mapping — normalize event name
  let event_name = _rybbit_map_event_name(parsed.eventName)
  let props = parsed.props

  // 3. SSO handling — rewrite to session_start when applicable
  if (event_name === 'sso') {
    const sso_result = _rybbit_handle_sso_event(category, props)
    if (!sso_result) return
    event_name = sso_result.eventName
    props = sso_result.props
  }

  // 4. Drop configured noisy / duplicate events
  if (RYBBIT_CONFIG.skipEvents.includes(event_name)) return

  // 5. Send
  rybbit_event(event_name, props)
}

// ---------------------------------------------------------------------------
// Private helpers (rybbit_event_from_custom internals)
// ---------------------------------------------------------------------------

function _rybbit_validate_action(action) {
  if (!action) return false
  if (typeof action === 'object') {
    return action.event && typeof action.event === 'string'
  }
  return true
}

function _rybbit_parse_ga4_event(action) {
  const event_name = action.event
  const props = {}

  if (!action.ecommerce) {
    return { eventName: event_name, props }
  }

  const ecommerce = action.ecommerce

  if (
    RYBBIT_CONFIG.itemListEvents.includes(event_name) &&
    ecommerce.items &&
    ecommerce.items.length > 0
  ) {
    const prices = ecommerce.items.map((item) => parseFloat(item.price) || 0)
    props.value = Math.min.apply(null, prices)
    props.items_count = ecommerce.items.length
  } else {
    const price_net = parseFloat(ecommerce.value) || 0
    const price_gross = parseFloat(ecommerce.live_price) || 0
    props.price_gross = price_gross || price_net
    props.price_net = price_net
    if (price_gross && price_net > 0) {
      props.markup = (price_gross - price_net).toFixed(2)
    }
  }

  if (ecommerce.transaction_id) props.transaction_id = ecommerce.transaction_id
  if (ecommerce.currency) props.currency = ecommerce.currency

  return { eventName: event_name, props }
}

function _rybbit_parse_standard_event(action, label, value, category) {
  const props = {}
  // Labels frequently carry an email address (user identifier). Hash any email
  // before it reaches Rybbit — GTM keeps the plaintext value separately.
  if (label) props.label = _hash_emails(label)
  if (value) props.value = parseFloat(value) || 0
  if (category) props.category = category
  return { eventName: action, props }
}

function _rybbit_map_event_name(event_name) {
  return RYBBIT_CONFIG.eventMap[event_name] || event_name
}

/**
 * Convert an SSO event into a `session_start` event when the URL carries the
 * full UTM pattern (token + utm_source + utm_campaign). Returns null when the
 * event must be dropped.
 */
function _rybbit_handle_sso_event(category, current_props) {
  // SSO fires multiple times with different categories; keep only the
  // `userBehaviour` one for Rybbit to avoid duplication.
  if (category !== 'userBehaviour') return null

  const url_params = new URLSearchParams(window.location.search)
  const token = url_params.get('token')
  const utm_source = url_params.get('utm_source')
  const utm_campaign = url_params.get('utm_campaign')

  if (!token || !utm_source || !utm_campaign) return null

  const props = {
    credit_available: current_props.value || 0,
    utm_source: utm_source,
    utm_campaign: utm_campaign,
  }

  const utm_medium = url_params.get('utm_medium')
  const utm_content = url_params.get('utm_content')
  const utm_term = url_params.get('utm_term')
  if (utm_medium) props.utm_medium = utm_medium
  if (utm_content) props.utm_content = utm_content
  if (utm_term) props.utm_term = utm_term

  return { eventName: 'session_start', props }
}

// ---------------------------------------------------------------------------
// Form-event helpers
// ---------------------------------------------------------------------------

/**
 * Thin facade for form-style events. Merges page-level `context` with the
 * call-site `data` and forwards to `rybbit_event` (size/PII safety net).
 *
 * @param {Object} params
 * @param {string} params.event   - e.g. "form-contatti:submit:success"
 * @param {Object} [params.data]  - { type, reason?, message?, invalid_fields? }
 * @param {Object} [params.context] - Pre-slimmed page-level context (no PII)
 */
export function track_form_event({ event, data = {}, context = {} } = {}) {
  if (!event || typeof event !== 'string') return
  rybbit_event(event, { ...context, ...data })
}

/**
 * Build an analytics-safe payload from vee-validate's `errors` slot.
 * Returns ONLY the names of invalid fields — never the messages.
 */
export function format_validation_errors(errors) {
  if (!errors || typeof errors !== 'object') {
    return { reason: 'Validation', invalid_fields: [] }
  }
  const invalid_fields = Object.entries(errors)
    .filter(([, value]) => Array.isArray(value) && value.length > 0)
    .map(([key]) => key)
  return { reason: 'Validation', invalid_fields }
}

/**
 * Normalize an axios-shaped error into an analytics-safe object:
 * { reason, status?, message? }.
 */
export function format_service_error(err) {
  if (!err) return { reason: 'Unknown' }

  const response = err.response
  if (response) {
    const data = response.data || {}
    const reason =
      data.code ||
      data.reason ||
      data.error ||
      (response.status ? `HTTP ${response.status}` : 'ServerError')
    const out = { reason }
    if (response.status) out.status = response.status
    const msg =
      typeof data.msg === 'string'
        ? data.msg
        : typeof data.message === 'string'
          ? data.message
          : err.message
    if (msg) out.message = msg
    return out
  }

  if (err.message) return { reason: 'Exception', message: err.message }
  return { reason: 'Unknown' }
}

/**
 * Vue mixin: exposes `track_form({ event, data, context })`.
 * Registered globally in src/main.js so every component has access.
 */
export const RybbitFormMixin = {
  methods: {
    track_form(params) {
      track_form_event({
        event: params && params.event,
        data: (params && params.data) || {},
        context: (params && params.context) || {},
      })
    },
  },
}
